/*
 * Ryan O'Dowd
 * 2025-07-18
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import Banner from '@components/common/Banner';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import EditSchoolDialog from '@website/components/EditSchoolDialog';
import Table from '@components/common/Table';
import Text from '@components/common/Text';
import TextInput from '@components/common/TextInput';
import Tooltip from '@components/common/Tooltip';
import {
  addSeats,
} from '@utils/store/actions';
import {
  format as dateFnsFormat,
} from 'date-fns';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const SEATS_IN_TIER_ONE = 1000000; // @TODO: 100;
// @TODO: const SEATS_IN_TIER_TWO = 1000000; // @TODO: 400;

const ManageSeatsDialog = (props: ManageSeatsDialogProps) => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();
  const dispatch = hooks.useTypedDispatch();

  const [_numSeatsToAddText, setNumSeatsToAddText] = useState('1');
  const [_isAddingSeats, setIsAddingSeats] = useState(false);
  const [_isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const _getHumanReadableSeatRange = (firstSeat, lastSeat) => {
    if (firstSeat === lastSeat) {
      return `seat ${firstSeat.toLocaleString()}`;
    }

    return `seats ${firstSeat.toLocaleString()}-${lastSeat.toLocaleString()}`;
  };

  const seatsTableRows: any = [
    [
      {
        id: 'tier_one_item',
        content: `Current seats (${_getHumanReadableSeatRange(1, Math.min(props.school.billing.total_billed_seats, SEATS_IN_TIER_ONE))})`,
      },
      {
        id: 'tier_one_number_of_seats',
        content: Math.min(props.school.billing.total_billed_seats, SEATS_IN_TIER_ONE),
      },
      {
        id: 'tier_one_cost_per_seat',
        content: utilities.prettyCurrency(globals.centsPerSeatTierOne),
      },
      {
        id: 'tier_one_total_cost',
        content: utilities.prettyCurrency(Math.min(SEATS_IN_TIER_ONE, props.school.billing.total_billed_seats) * globals.centsPerSeatTierOne),
      },
    ],
  ];

  if (props.school.billing.total_billed_seats > SEATS_IN_TIER_ONE) {
    seatsTableRows.push([
      {
        id: 'tier_two_item',
        content: `Next renewal seats (${_getHumanReadableSeatRange(SEATS_IN_TIER_ONE + 1, props.school.billing.total_billed_seats)})`,
      },
      {
        id: 'tier_two_number_of_seats',
        content: props.school.billing.total_billed_seats - SEATS_IN_TIER_ONE,
      },
      {
        id: 'tier_two_cost_per_seat',
        content: utilities.prettyCurrency(globals.centsPerSeatTierTwo),
      },
      {
        id: 'tier_two_total_cost',
        content: utilities.prettyCurrency((props.school.billing.total_billed_seats - SEATS_IN_TIER_ONE) * globals.centsPerSeatTierTwo),
      },
    ]);
  }

  const invoicesTableRows = props.school.billing.transactions.map((transaction) => {
    return [
      {
        id: `${transaction.id}_id`,
        content: <Text>{`${props.school.id}`.padStart(4, '0')}-{`${transaction.id}`.padStart(5, '0')}</Text>, // @TODO: should probably have a transaction id, but i don't like using the db column id
        sortValue: +transaction.id,
      },
      {
        id: `${transaction.id}_created_at`,
        content: <Tooltip title={dateFnsFormat(transaction.created_at, 'hh:mm a')}><Text>{dateFnsFormat(new Date(transaction.created_at), 'MMMM d, yyyy')}</Text></Tooltip>,
        sortValue: new Date(transaction.created_at),
      },
      {
        id: `${transaction.id}_number_of_seats`,
        content: <Text>{`${transaction.number_of_seats} ${transaction.number_of_seats === 1 ? 'seat' : 'seats'} ${transaction.number_of_seats < 0 ? 'removed' : 'added'}`}</Text>,
        sortValue: +transaction.number_of_seats,
      },
      {
        id: `${transaction.id}_cost`,
        content: <Text>{utilities.prettyCurrency(transaction.total_cost_of_seats * 100)}</Text>,
        sortValue: +transaction.total_cost_of_seats,
      },
      // @TODO: pdf download link
    ];
  });


  let numSeatsToAddTierOne = 0;
  const numSeatsToAdd = +_numSeatsToAddText;
  if (props.school.billing.total_billed_seats < SEATS_IN_TIER_ONE) {
    numSeatsToAddTierOne = Math.min(SEATS_IN_TIER_ONE - props.school.billing.total_billed_seats, numSeatsToAdd);
    seatsTableRows.push([
      {
        id: 'new_tier_one_item',
        content: `New seats (${_getHumanReadableSeatRange(props.school.billing.total_billed_seats + 1, props.school.billing.total_billed_seats + numSeatsToAddTierOne)})`,
        textStyles: styles.currentRowText,
      },
      {
        id: 'new_tier_one_number_of_seats',
        content: numSeatsToAddTierOne.toLocaleString(),
        textStyles: styles.currentRowText,
      },
      {
        id: 'new_tier_one_cost_per_seat',
        content: utilities.prettyCurrency(globals.centsPerSeatTierOne),
        textStyles: styles.currentRowText,
      },
      {
        id: 'new_tier_one_total_cost',
        content: `${utilities.prettyCurrency(numSeatsToAddTierOne * globals.centsPerSeatTierOne)}${props.isInTrial ? '' : ' (due today)'}`,
        textStyles: styles.currentRowText,
      },
    ]);
  }

  let numSeatsToAddTierTwo = 0;
  if ((props.school.billing.total_billed_seats + numSeatsToAdd) > SEATS_IN_TIER_ONE) {
    numSeatsToAddTierTwo = numSeatsToAdd - numSeatsToAddTierOne;
    seatsTableRows.push([
      {
        id: 'new_tier_two_item',
        content: `New seats (${_getHumanReadableSeatRange(props.school.billing.total_billed_seats + numSeatsToAddTierOne + 1, props.school.billing.total_billed_seats + numSeatsToAddTierOne + numSeatsToAddTierTwo)})`,
        textStyles: styles.currentRowText,
      },
      {
        id: 'new_tier_two_number_of_seats',
        content: numSeatsToAddTierTwo.toLocaleString(),
        textStyles: styles.currentRowText,
      },
      {
        id: 'new_tier_two_cost_per_seat',
        content: utilities.prettyCurrency(globals.centsPerSeatTierTwo),
        textStyles: styles.currentRowText,
      },
      {
        id: 'new_tier_two_total_cost',
        content: `${utilities.prettyCurrency(numSeatsToAddTierTwo * globals.centsPerSeatTierTwo)}${props.isInTrial ? '' : ' (due today)'}`,
        textStyles: styles.currentRowText,
      },
    ]);
  }

  const totalsRows: any = [
    [
      {
        id: 'todays_total_item',
        content: "Today's total",
        textStyles: [styles.currentRowText, styles.totalsRowText],
      },
      {
        id: 'todays_total_number_of_seats',
        content: numSeatsToAdd.toLocaleString(),
        textStyles: [styles.currentRowText, styles.totalsRowText],
      },
      {
        id: 'todays_total_cost_per_seat',
        content: '',
        textStyles: [styles.currentRowText, styles.totalsRowText],
      },
      {
        id: 'todays_total_total_cost',
        content: `${utilities.prettyCurrency(
          (!props.isInTrial ? 0 : (props.school.billing.total_billed_seats * globals.centsPerSeatTierOne)) + // @TODO: not all current seats are guaranteed to be the same price
          (numSeatsToAddTierOne * globals.centsPerSeatTierOne) +
          (numSeatsToAddTierTwo * globals.centsPerSeatTierTwo) // eslint-disable-line @stylistic/comma-dangle
        )}`,
        textStyles: [styles.currentRowText, styles.totalsRowText],
      },
    ],
  ];

  if (!props.isInTrial) {
    totalsRows.push([
      {
        id: 'grand_total_item',
        content: `Total due at next renewal (${dateFnsFormat(new Date(props.school.billing.renewal_date), 'MMMM d, yyyy')})`, // @TODO: if currently in trial, this should be one year from today
      },
      {
        id: 'grand_total_number_of_seats',
        content: (props.school.billing.total_billed_seats + numSeatsToAdd).toLocaleString(),
      },
      {
        id: 'grand_total_cost_per_seat',
        content: '',
      },
      {
        id: 'grand_total_total_cost',
        content: `${utilities.prettyCurrency(
          ((Math.min(props.school.billing.total_billed_seats, SEATS_IN_TIER_ONE) + numSeatsToAddTierOne) * globals.centsPerSeatTierOne) +
          ((Math.max(0, Math.min(props.school.billing.total_billed_seats - SEATS_IN_TIER_ONE)) + numSeatsToAddTierTwo) * globals.centsPerSeatTierTwo) // eslint-disable-line @stylistic/comma-dangle
        )}`,
      },
    ]);
  }

  return (
    <Dialog
      visible={true}
      onDismiss={() => props.onClose()} // @TODO: ask about unsaved changes
    >
      <Dialog.Title>Add seats</Dialog.Title>{/* @TODO: i18n */}
      <Dialog.Content>
        {props.isInTrial &&
          <span style={styles.publishedStatusWrapper}>
            <Banner
              type='info'
              text='Your subscription will begin upon checkout.'// @TODO: wordsmith this
            />
          </span>
        }
        <TextInput
          mode='outlined'
          value={`${numSeatsToAdd}`}
          onChangeText={(text) => setNumSeatsToAddText(text.replace(/[^0-9]/g, ''))}// @TODO: should numbersOnly be a prop?
          label='Number of seats to add'
        />
        <Table
          tableTitle='All seats'
          open={true}
          disableAccordion={true}
          headerRowData={[
            {width: 220, id: 'type', content: 'Item'}, // @TODO: better name than item
            {width: 100, id: 'number_of_seats', content: '# seats', rightAlign: true},
            {width: 100, id: 'cost_per_seat', content: 'Cost per seat', rightAlign: true},
            {width: 100, id: 'total_cost', content: 'Total', rightAlign: true},
          ]}
          isSortable={false}
          rowData={seatsTableRows}// @TODO: shouldn't be clickable (or if they are, make individually updatable)
          totalsRowsData={totalsRows}// @TODO: shouldn't be clickable (or if they are, make individually updatable)
        />

        <Table
          tableTitle='Past invoices'
          open={false}
          headerRowData={[
            {width: 50, id: 'id', content: 'Transaction ID'},
            {width: 70, id: 'created_at', content: 'Date'},
            {width: 70, id: 'number_of_seats', content: '# seats', rightAlign: true},
            {width: 70, id: 'total_cost_of_seats', content: 'Amount', rightAlign: true},
            // @TODO: pdf download
          ]}
          isSortable={true}
          orderByIndex={0}
          rowData={invoicesTableRows}// @TODO: shouldn't be clickable (or if they are, make individually updatable)
        />
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          mode='text'
          onPress={() => props.onClose()}
        >
          {i18n('cancel')}
        </Button>

        {!props.school.billing?.stripe_payment_method_id
          ? (
            <Button
              onPress={() => setIsEditDialogOpen(true)}
            >{/* @TODO: i18n */}
              Update payment method to add seats
            </Button>
          )
          : (
            <Button
              disabled={_isAddingSeats}
              loading={_isAddingSeats}
              onPress={() => {
                setIsAddingSeats(true);
                dispatch(addSeats(props.school.id, numSeatsToAdd, () => {
                  setIsAddingSeats(false);
                  alert(`${numSeatsToAdd} seats added successfully!`); // @TODO: make this prettier
                  props.onClose();
                }));
              }}
            >{/* @TODO: i18n */}
              Checkout
            </Button>
          )
        }

        {_isEditDialogOpen &&
          <EditSchoolDialog
            school={props.school}
            onClose={() => setIsEditDialogOpen(false)}
          />
        }
      </Dialog.Actions>
    </Dialog>
  );
};

class ManageSeatsDialogProps {
  onClose: () => void;
  school: any;
  isInTrial: boolean;
};

export default ManageSeatsDialog;
